import React from 'react';
import * as Sentry from '@sentry/react';
import { STALE_BUILD_COPY, isChunkLoadFailure } from '../../utils/chunkLoadError';
import css from './LazyRouteBoundary.module.scss';

/**
 * The boundary a lazily-loaded route needs OUTSIDE its own chunk.
 *
 * A boundary shipped inside the chunk it is meant to protect cannot catch that
 * chunk failing to arrive — it is not on the page yet. `ConsoleErrorBoundary`
 * is in the admin chunk, so it covers the pages below the console layout and
 * cannot cover the console layout itself. Without this one, an admin sitting on
 * /dashboard who clicks Admin after a deploy gets a `ChunkLoadError` all the way
 * to the boundary in `index.tsx`, which replaces the entire application with a
 * single line of text. That is the FIRST console navigation of a session, which
 * is the one most likely to be crossing a release.
 *
 * Written in plain markup on purpose. It lives in the main bundle, where every
 * user pays for it, and the antd `Result` that `ConsoleErrorBoundary` uses cost
 * 17 kB gzipped when it was added to the admin chunk. Reusing that component
 * here would pull all of it into `main.js` and undo most of the split this
 * feature exists to create. Only the detection and the wording are shared, via
 * `utils/chunkLoadError`, because those are the parts where drift would be a
 * defect rather than a difference in scale.
 */
class LazyRouteBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.withScope((scope) => {
      // Same tag vocabulary as the console's own boundary, with the scope that
      // distinguishes them: a failure here means the shell never loaded, which
      // is a worse outcome than one page dying inside it.
      scope.setTag('route.chunk_error', isChunkLoadFailure(error) ? 'chunk_load' : 'render');
      scope.setTag('route.boundary', 'shell');
      scope.setContext('react', { componentStack: info.componentStack });
      Sentry.captureException(error);
    });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = isChunkLoadFailure(error);
    return (
      <div className={css.failure} role="alert">
        <h2 className={css.title}>{stale ? STALE_BUILD_COPY.title : 'This page stopped working'}</h2>
        <p className={css.detail}>
          {stale
            ? STALE_BUILD_COPY.detail
            : 'The menu still works, so you can move to another page. Reloading may also clear it.'}
        </p>
        <button
          type="button"
          className={css.action}
          onClick={
            // A stale build has exactly one fix. A render failure might not
            // recur, so that one re-mounts in place first.
            stale ? () => window.location.reload() : () => this.setState({ error: null })
          }
        >
          {stale ? STALE_BUILD_COPY.action : 'Try again'}
        </button>
      </div>
    );
  }
}

export default LazyRouteBoundary;
